<template>
  <div class="stage-detail px-2 py-1">
    <div ref="stageChartContent">
      <div class="caption text--secondary">
        {{ selectedObservatory.address }}
      </div>

      <v-card class="d-flex align-center" flat tile>
        <v-btn-toggle
          v-if="hasForecast"
          v-model="displayHours"
          mandatory
          dense
          class="mr-3 mb-1"
        >
          <v-btn small :value="6" class="px-1">
            6時間予測
          </v-btn>
          <v-btn small :value="36" class="px-1">
            36時間予測
          </v-btn>
        </v-btn-toggle>
        <v-btn-toggle v-model="displayInterval" mandatory dense class="mb-1">
          <v-btn small :value="10" class="px-1">
            10分毎
          </v-btn>
          <v-btn small :value="60" class="px-1">
            時間毎
          </v-btn>
        </v-btn-toggle>
      </v-card>

      <div class="caption mt-1" v-if="isChartVisible">（{{ levelUnit }}）</div>
      <StageChart
        v-if="isChartVisible"
        :height="isMobile ? 180 : 240"
        :selected-observatory="selectedObservatory"
        :chartTimeSeries="timeSeries"
        :minDate="minDate"
        :maxDate="maxDate"
        :levelUnit="levelUnit"
        :chartWaterBoxLevel="chartWaterBoxLevel"
      />

      <div v-if="isChartVisible">
        <template v-for="(std, i) in standardLevels">
          <div
            class="caption d-flex"
            :key="i"
            v-if="std.level !== undefined"
            style="line-height: 1rem"
          >
            <v-card width="70" flat tile>
              <div
                class="standard-color ml-8"
                :style="{ 'border-bottom-color': std.color }"
              ></div>
            </v-card>
            <v-card width="100" flat tile>{{ std.label }}</v-card>
            <v-card width="100" flat tile class="text-right">
              {{ std.level == undefined ? "---" : std.level + " " + levelUnit }}
            </v-card>
          </div>
        </template>
      </div>
    </div>

    <div class="mt-5 caption" v-if="timeSeries.length === 0">
      <template v-if="isStageKiki === true && chartWaterBoxLevel !== null">
        この観測所は観測開始水位以下です。
      </template>
      <template v-else>
        この観測所は欠測しています。
      </template>
    </div>

    <v-simple-table
      dense
      class="mt-1"
      v-if="timeSeries.length > 0"
      fixed-header
      :height="tableHeight"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">日時</th>
            <th class="text-left">水位 ({{ levelUnit }})</th>
            <th style="width: 20px"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(ts, index) in timeSeries"
            :key="index"
            :class="{ forecat: ts.type === 'forecast' }"
            :id="`row-${index}`"
          >
            <td>{{ ts.jst.format("MM/DD HH:mm") }}</td>
            <td class="text-right">
              <v-chip
                :style="getStageChipStyle(selectedObservatory, ts.level)"
                label
              >
                {{ ts.level === null ? "---" : ts.level }}
              </v-chip>
            </td>
            <td></td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import StageChart from "./StageChart";
import { OBSERVATORY, STAGES } from "../../../enums/Type";
import moment from "moment";
import {
  isPrivateMode,
  isPublicMode,
  getStageChipStyle
} from "../../../utils/common";

export default {
  name: "StageDetail",
  components: {
    StageChart
  },
  data() {
    return {
      displayHours: 1,
      displayInterval: 10,
      tableHeight: 0
    };
  },
  mounted() {
    setTimeout(() => {
      this.calcTableHeight();
      this.scrollNow();
    }, 500);
  },
  computed: {
    selectedObservatory() {
      return this.$store.getters.selectedObservatory;
    },
    levelUnit() {
      return this.selectedObservatory.kind === 9 ? "TP.m" : "m";
    },
    hasForecast() {
      if (isPublicMode || this.selectedObservatory.timeSeries === undefined) {
        return false;
      }
      return this.selectedObservatory.timeSeries.some(
        ts => ts.type === "forecast"
      );
    },
    mesuredLatestValue() {
      const date = this.baseDate.clone();
      return date.format("YYYY/MM/DD HH:mm");
    },
    baseDate() {
      return this.$store.getters.baseDate.clone();
    },
    minDate() {
      return this.baseDate
        .clone()
        .subtract(this.displayInterval === 10 ? 3 : 23, "hours");
    },
    maxDate() {
      return this.baseDate.clone().add(this.displayHours, "hours");
    },
    isStageKiki() {
      return this.selectedObservatory.type === OBSERVATORY.STAGE_KIKI.code;
    },
    standardLevels() {
      const levels = [];

      if (this.isStageKiki === true) {
        levels.push({
          label: "氾濫開始水位",
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.floodLevel
        });
        levels.push({
          label: "危険水位",
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.warnLevel
        });
        levels.push({
          label: STAGES.START.label,
          color: STAGES.START.color,
          level: this.selectedObservatory.startLevel
        });
      } else {
        levels.push({
          label: STAGES.OUTBREAK.label,
          color: STAGES.OUTBREAK.color,
          level: this.selectedObservatory.outbreakLevel
        });
        levels.push({
          label: STAGES.DANGEROUS.label,
          color: STAGES.DANGEROUS.color,
          level: this.selectedObservatory.dangerousLevel
        });
        levels.push({
          label: STAGES.EVACUATION.label,
          color: STAGES.EVACUATION.color,
          level: this.selectedObservatory.evacuationLevel
        });
        levels.push({
          label: STAGES.WARNING.label,
          color: STAGES.WARNING.color,
          level: this.selectedObservatory.warningLevel
        });
        levels.push({
          label: STAGES.STANDBY.label,
          color: STAGES.STANDBY.color,
          level: this.selectedObservatory.standbyLevel
        });
      }

      return levels;
    },
    timeSeries() {
      let targets = this.selectedObservatory.timeSeries.filter(
        ts =>
          ts.date > this.minDate.format("YYYY/MM/DD HH:mm") &&
          ts.date <= this.maxDate.format("YYYY/MM/DD HH:mm")
      );

      if (this.displayInterval === 60) {
        const reals = targets.filter(
          ts => ts.type === "real" && ts.date.slice(-2) === "00"
        );
        let forecasts = targets.filter(ts => ts.type === "forecast");
        if (forecasts.length > 0) {
          let mm = forecasts[0].date.slice(-2);
          if (forecasts.some(f => f.date.slice(-2) === "00")) {
            mm = "00";
          }
          forecasts = forecasts.filter(f => f.date.slice(-2) === mm);
        }
        targets = reals.concat(forecasts);
      }

      return targets.map(r => {
        return {
          date: r.date,
          jst: moment.utc(r.date, "YYYY/MM/DD HH:mm").local(),
          type: r.type,
          level: r.level !== undefined ? parseFloat(r.level).toFixed(2) : null,
          flow: r.flow !== undefined ? parseFloat(r.flow).toFixed(2) : null
        };
      });
    },
    chartWaterBoxLevel() {
      const target = this.$store.getters.observatories.find(
        o =>
          o.id === this.selectedObservatory.id &&
          o.type === OBSERVATORY.STAGE_KIKI.code &&
          o.chartWaterBoxLevel !== undefined
      );
      return target === undefined ? null : target.chartWaterBoxLevel;
    },
    isChartVisible() {
      return this.timeSeries.length > 0 || this.chartWaterBoxLevel !== null;
    },
    isMobile() {
      return this.$route.path === "/mobile";
    }
  },
  watch: {
    selectedObservatory() {
      this.calcTableHeight();
    },
    hasForecast(newval, oldval) {
      if (newval === false && oldval === true) {
        this.displayHours = 1;
      } else if (isPrivateMode && newval === true && oldval === false) {
        this.displayHours = 6;
      }
    },
    timeSeries() {
      setTimeout(() => {
        this.scrollNow();
      }, 500);
    }
  },
  methods: {
    getStageChipStyle,
    calcTableHeight() {
      const chartContentHeight = this.$refs.stageChartContent.clientHeight;
      const parentHeight = window.innerHeight * (this.isMobile ? 0.9 : 1);
      const offset = 50;
      this.tableHeight = parentHeight - chartContentHeight - offset;
    },
    scrollNow() {
      const reals = this.timeSeries.filter(row => row.type === "real");
      if (reals.length === 0) return;
      const targetIndex = reals.length - 1;
      if (targetIndex < 0) return;
      const id = `#row-${targetIndex}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      const top = height * (targetIndex - (this.isMobile ? 3 : 5));
      tableWrapper.scroll({ top, behavior: "instant" });
    }
  }
};
</script>

<style lang="scss" scoped>
.stage-detail {
  thead {
    background-color: #addff3;
  }
  tbody td {
    background: #eee;
  }
  tbody tr:nth-child(odd) td {
    background: #fff;
  }
  .standard-color {
    width: 16px;
    height: 10px;
    border-bottom: white 3px solid;
  }
  .forecat {
    color: blue;
  }
}
</style>
